/**
 * Objetivos trimestrales de producto — persistidos en app_config.
 * Key: admin_product_quarter_goals
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export const PRODUCT_GOALS_CONFIG_KEY = 'admin_product_quarter_goals';

export type ProductGoalKpiKey =
    | 'registrations'
    | 'active_users'
    | 'captures'
    | 'onboarding_v2_completed'
    | 'store_orders';

export interface ProductQuarterGoal {
    id: string;
    label: string;
    kpi_key: ProductGoalKpiKey;
    target: number;
}

export interface ProductQuarterGoalsConfig {
    quarter_label: string;
    goals: ProductQuarterGoal[];
    updated_at?: string;
}

export function defaultQuarterGoals(quarterLabel: string): ProductQuarterGoalsConfig {
    return {
        quarter_label: quarterLabel,
        goals: [
            { id: 'registrations', label: 'Registros nuevos', kpi_key: 'registrations', target: 500 },
            { id: 'captures', label: 'Capturas', kpi_key: 'captures', target: 2000 },
            { id: 'onboarding_v2_completed', label: 'Onboarding OK', kpi_key: 'onboarding_v2_completed', target: 300 },
        ],
        updated_at: new Date().toISOString(),
    };
}

function parseGoalsConfig(raw: unknown, fallbackLabel: string): ProductQuarterGoalsConfig {
    if (!raw || typeof raw !== 'object') {
        return defaultQuarterGoals(fallbackLabel);
    }
    const obj = raw as Record<string, unknown>;
    const goals = Array.isArray(obj.goals)
        ? (obj.goals as ProductQuarterGoal[]).filter(g => g.kpi_key && g.target > 0)
        : [];
    if (goals.length === 0) {
        return defaultQuarterGoals(fallbackLabel);
    }
    return {
        quarter_label: typeof obj.quarter_label === 'string' && obj.quarter_label
            ? obj.quarter_label
            : fallbackLabel,
        goals,
        updated_at: typeof obj.updated_at === 'string' ? obj.updated_at : undefined,
    };
}

export function useProductQuarterGoals(quarterLabel: string) {
    const queryClient = useQueryClient();

    const query = useQuery<ProductQuarterGoalsConfig>({
        queryKey: ['admin', 'product-quarter-goals'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_all_config');
            if (error) throw error;
            const items = normalizeRpcResponse(data) as Array<{ key: string; value: unknown }>;
            const item = items.find(i => i.key === PRODUCT_GOALS_CONFIG_KEY);
            return parseGoalsConfig(item?.value, quarterLabel);
        },
        staleTime: 60_000,
    });

    const saveMutation = useMutation({
        mutationFn: async (config: ProductQuarterGoalsConfig) => {
            const payload: ProductQuarterGoalsConfig = {
                ...config,
                updated_at: new Date().toISOString(),
            };
            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: PRODUCT_GOALS_CONFIG_KEY,
                p_value: payload,
            });
            if (error) throw error;
            return payload;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'product-quarter-goals'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
        },
    });

    return { ...query, saveGoals: saveMutation.mutateAsync, isSaving: saveMutation.isPending };
}

export function goalProgressPct(actual: number, target: number): number {
    if (target <= 0) return 0;
    return Math.min(100, Math.round((actual / target) * 100));
}

/** No extrapolar el trimestre con menos de dos semanas de datos. */
export const QUARTER_PROJECTION_MIN_DAYS = 14;

export function projectedQuarterPct(actual: number, target: number, daysElapsed: number, daysTotal: number): number | null {
    if (target <= 0 || daysElapsed < QUARTER_PROJECTION_MIN_DAYS || daysTotal <= 0) return null;
    const projected = (actual / daysElapsed) * daysTotal;
    return Math.round((projected / target) * 100);
}
